import moment from "moment"
import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { BanknoteArrowDown, CircleCheck, CircleHelp, CircleX, Clock, PackageCheck, TicketSlash, Truck } from "lucide-react"
import { Vendor } from "./types";
import { CustomBadge } from "@/components/elements/CustomBadge";

/* ============== Class niiluuleh ============== */

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function classNames(...classes: ClassValue[]) {
  return classes.filter(Boolean).join(' ')
}

export function isCustomerAuthActive(vendor: any): boolean {
  if (!vendor) return false
  if (vendor.canAuth === false) return false
  if (Array.isArray(vendor.modules) && !vendor.modules.includes('customers')) return false
  return true
}

/*
  Two independent layers have to agree before a shopper sees these.

    module  — whether the vendor has the feature at all. `vendor.modules` is
              already filtered by plan on the server, so the slug being present
              covers the subscription flag too.
    boolean — the vendor's own "temporarily off for customers" switch in
              /settings, which they can flip without giving up the feature.

  The Array.isArray guard keeps this fail-open: a payload that never carried
  `modules` shouldn't silently strip a working feature out of checkout. Same
  shape as isCustomerAuthActive above.
*/
export function isGiftCardActive(vendor: any): boolean {
  if (!vendor) return false
  if (!vendor.useGiftCard) return false
  if (Array.isArray(vendor.modules) && !vendor.modules.includes('gift-card')) return false
  return true
}

// Loyalty has no per-vendor storefront boolean — the module slug (plan-filtered
// server-side) is the whole gate; program.isActive is enforced by the backend.
export function isLoyaltyActive(vendor: any): boolean {
  if (!vendor) return false
  if (Array.isArray(vendor.modules) && !vendor.modules.includes('loyalty')) return false
  return true
}

export function buildLoyaltyCategoryParents(categories: any[]): Record<string, string | null> {
  const parents: Record<string, string | null> = {}
  const walk = (nodes: any[], parentId: string | null) => {
    for (const node of nodes || []) {
      parents[node.id] = parentId
      walk(node.children || [], node.id)
    }
  }
  walk(categories || [], null)
  return parents
}

// The loyalty product-list tab is personal: it only makes sense when the
// customer's own tier carries a discount benefit (mirrors the backend filter,
// which resolves the customer's tier and returns nothing otherwise).
export function hasLoyaltyTierBenefits(summary: any): boolean {
  return !!summary?.currentTier?.benefits?.some((benefit: any) => (benefit.type === 'percentDiscount' && benefit.valuePercent > 0) || (benefit.type === 'fixedDiscount' && benefit.valueAmount > 0))
}

// Display-only mirror of the backend's loyaltyCheckout eligibility (matchesTarget /
// isProductEligible / per-line best percent saving) — the checkout preview endpoint
// stays authoritative for money; this just decides whether a product shows the
// customer's tier discount. categoryParents maps categoryId → parentId so category
// targets match descendants, like the server's ancestor expansion.
// product.categories comes in two shapes: raw uuid[] (cart snapshots) or full
// category objects with their own `parents` ancestor list (product list/detail
// jsonb_agg) — both are normalized here.
export function getLoyaltyProductDiscount({ product, variant, summary, categoryParents, hasOtherDiscount }: { product: any, variant: any, summary: any, categoryParents: Record<string, string | null>, hasOtherDiscount: boolean }) {
  if (!summary?.currentTier?.benefits?.length || !variant?.price || variant.price <= 0) return null
  if (hasOtherDiscount && !summary.stackWithDiscounts) return null
  const expanded = new Set<string>()
  const addWithAncestors = (id: string | null) => {
    let current = id
    let guard = 0
    while (current && !expanded.has(current) && guard < 20) {
      expanded.add(current)
      current = categoryParents?.[current] || null
      guard++
    }
  }
  for (const entry of product?.categories || []) {
    const categoryId = typeof entry === 'string' ? entry : entry?.id
    if (!categoryId) continue
    addWithAncestors(categoryId)
    if (entry && typeof entry === 'object' && Array.isArray(entry.parents)) {
      for (const parentId of entry.parents) addWithAncestors(parentId)
    }
  }
  const matches = (target: any) => {
    if (target.targetType === 'product') {
      if (target.targetId !== product?.id) return false
      if (target.variantIds?.length && variant?.id && !target.variantIds.includes(variant.id)) return false
      return true
    }
    if (target.targetType === 'category') return expanded.has(target.targetId)
    return false
  }
  const isEligible = (targets: any[]) => {
    const includes = (targets || []).filter((target: any) => target.role === 'include')
    const excludes = (targets || []).filter((target: any) => target.role === 'exclude')
    if (excludes.some(matches)) return false
    if (!includes.length) return true
    return includes.some(matches)
  }
  const price = variant.price
  const priceSale = variant.priceSale && variant.priceSale > 0 && variant.priceSale < price ? variant.priceSale : null
  let best: any = null
  for (const benefit of summary.currentTier.benefits) {
    if (benefit.type !== 'percentDiscount' || !benefit.valuePercent) continue
    if (!isEligible(benefit.targets)) continue
    let unitSaving
    if (priceSale && !summary.stackWithSale) unitSaving = Math.max(0, Math.round(price * benefit.valuePercent / 100) - (price - priceSale))
    else unitSaving = Math.round((priceSale && summary.stackWithSale ? priceSale : price) * benefit.valuePercent / 100)
    if (unitSaving <= 0) continue
    if (!best || unitSaving > best.unitSaving) best = { percent: benefit.valuePercent, unitSaving, saleOffset: priceSale && !summary.stackWithSale ? price - priceSale : 0, finalPrice: (priceSale ?? price) - unitSaving, tierName: summary.currentTier.name }
  }
  return best
}

// Coupon codes are discount records of type 'code', so the discount module owns
// them — matching POST /discount/validate, which refuses the code without it.
export function isCouponActive(vendor: any): boolean {
  if (!vendor) return false
  if (!vendor.useCoupon) return false
  if (Array.isArray(vendor.modules) && !vendor.modules.includes('discount')) return false
  return true
}

export function getCheckoutPath(vendor: any, isLoggedIn: boolean): string {
  if (!isCustomerAuthActive(vendor)) return '/checkout'
  return isLoggedIn ? '/checkout' : '/checkout/login'
}

export function getCouponPriceForVariant(variant: any, appliedCoupon: any, productData: any): number | null {
  if (!appliedCoupon || !variant) return null
  const discount = appliedCoupon.discount || appliedCoupon
  if (discount.type !== 'productDiscount') return null
  const codeDiscount = productData?.discounts?.find((d: any) =>
    d.type === 'productDiscount' && d.method === 'code' && d.code === discount.code
  )
  if (!codeDiscount) return null
  if (codeDiscount.variantIds?.length && !codeDiscount.variantIds.includes(variant?.id)) return null
  const baseForCoupon = codeDiscount.discountBase === 'original'
    ? variant.price
    : (variant.priceSale && variant.priceSale > 0 && variant.priceSale < variant.price ? variant.priceSale : variant.price)
  if (codeDiscount.valueType === 'percent' && codeDiscount.valuePercent) {
    return Math.max(0, Math.round(baseForCoupon * (1 - codeDiscount.valuePercent / 100)))
  }
  if (codeDiscount.valueType === 'amount' && codeDiscount.valueAmount) {
    return Math.max(0, baseForCoupon - codeDiscount.valueAmount)
  }
  if (codeDiscount.valueType === 'free') return 0
  return null
}
/* ============== Check inputs ============== */

export const isValidEmail = (email: string) => { // only email format
  if (!email) return false
  const regex = new RegExp(/^[-!#-'*+\/-9=?^-~]+(?:\.[-!#-'*+\/-9=?^-~]+)*@[-!#-'*+\/-9=?^-~]+(?:\.[-!#-'*+\/-9=?^-~]+)+$/, "i")
  return regex.test(email)
}

export const isValidPhoneNumber = (phone: string) => { // all types of phone numbers
  if (!phone) return false
  if (phone.length < 6) return false
  const regex = new RegExp(/^[+]?(?:\(\d+(?:\.\d+)?\)|\d+(?:\.\d+)?)(?:[ -]?(?:\(\d+(?:\.\d+)?\)|\d+(?:\.\d+)?))*(?:[ ]?(?:x|ext)\.?[ ]?\d{1,5})?$/)
  return regex.test(phone)
}

export const isValidName = (name: string) => { // Mongolian and English letters, hyphen, apostrophe and space
  if (!name) return false
  const regex = new RegExp('^[\u04ae\u04af\u04e8\u04e9\u0401\u0451\u0410-\u044fa-zA-Z-\' ]+$')
  return regex.test(name)
}

export const isValidUsername = (username: string) => { // only letters, numbers, dots and underscores
  if (!username) return false
  const regex = new RegExp(/^[a-zA-Z0-9._]+$/)
  return regex.test(username)
}

export const isNotEmpty = (value: any) => {
  if (value === undefined || value === null || value === '') return false
  const check = value.split(' ').join('').split('\n').join('')
  if (check === '') return false
  return true
}


/* ============== Check JS variables ============== */

export const isValidBoolean = (something: boolean) => {
  if (typeof something === "boolean") return true
  else return false
}

export const isValidArray = (array: Array<any>) => {
  if (Array.isArray(array)) return true
  else return false
}


/* ============== Utils ============== */

export function formatImageURL(image: string, root = false) { // Use this function if you are using images from bucket CDN
  const urlPattern = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i
  let bucket = ''
  if (process.env.NEXT_PUBLIC_BUCKET) {
    if (root) bucket = process.env.NEXT_PUBLIC_BUCKET
    else bucket = process.env.NEXT_PUBLIC_BUCKET + 'uploads/'
  }
  if (urlPattern.test(image)) return image
  else return bucket ? bucket + image : image
}

export function formatImageURLGoogle(image: string, root = false) { // Use this function if you are using images from bucket 
  const urlPattern = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i
  let bucket = ''
  if (process.env.NEXT_PUBLIC_BUCKET_GOOGLE) {
    if (root) bucket = process.env.NEXT_PUBLIC_BUCKET_GOOGLE
    else bucket = process.env.NEXT_PUBLIC_BUCKET_GOOGLE + 'uploads/'
  }
  if (urlPattern.test(image)) return image
  else return bucket ? bucket + image : image
}

export function formatNumber(number: any) { // 12000 => 12,000
  number = number.toString()
  const pattern = /(-?\d+)(\d{3})/
  while (pattern.test(number))
    number = number.replace(pattern, "$1,$2")
  return number
}

export function formatPrice(amount: any) { // 12000 => 12,000₮
  if (amount === undefined || amount === null) return false
  amount = amount.toString()
  const pattern = /(-?\d+)(\d{3})/
  while (pattern.test(amount))
    amount = amount.replace(pattern, "$1,$2")
  return amount + '₮'
}

export function timeAgo(date: Date) { // 2024-04-10T12:00:00.000Z => 1ц өмнө
  const now = new Date();
  date = new Date(date);
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) {
    return `Яг одоо`
  } else if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    return `${minutes}${minutes === 1 ? 'м' : 'м'} өмнө`
  } else if (seconds < 86400) {
    const hours = Math.floor(seconds / 3600);
    return `${hours}${hours === 1 ? 'ц' : 'ц'} өмнө`
  } else if (seconds < 2592000) {
    const days = Math.floor(seconds / 86400);
    return `${days}${days === 1 ? 'ө' : 'ө'} өмнө`
  } else if (seconds < 31536000) {
    const months = Math.floor(seconds / 2592000);
    return `${months}${months === 1 ? ' сарын' : 'сарын'} өмнө`
  } else {
    const years = Math.floor(seconds / 31536000);
    return `${years}${years === 1 ? ' жилийн' : 'жилийн'} өмнө`
  }
}

export function timeAgoShort(date: Date) { // 2024-04-10T12:00:00.000Z => 1ц
  const now = new Date();
  date = new Date(date);
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) {
    return `Одоо`
  } else if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    return `${minutes}${minutes === 1 ? 'м' : 'м'}`
  } else if (seconds < 86400) {
    const hours = Math.floor(seconds / 3600);
    return `${hours}${hours === 1 ? 'ц' : 'ц'}`
  } else if (seconds < 2592000) {
    const days = Math.floor(seconds / 86400);
    return `${days}${days === 1 ? 'ө' : 'ө'}`
  } else if (seconds < 31536000) {
    const months = Math.floor(seconds / 2592000);
    return `${months}${months === 1 ? ' сар' : 'сар'}`
  } else {
    const years = Math.floor(seconds / 31536000);
    return `${years}${years === 1 ? ' жил' : 'жил'}`
  }
}

export function formatDateNormal(date: Date) { // 2024-04-10T12:00:00.000Z => 2024.04.10
  date = new Date(date);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear());
  return `${year}.${month}.${day}`;
}

export function formatDateTimeNormal(date: Date) { // 2024-04-10T12:00:00.000Z => 2024.04.10 12:00
  date = new Date(date);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear());
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}.${month}.${day} ${hours}:${minutes}`;
}

export function formatDateLong(date: Date) { // 2024-04-10T12:00:00.000Z => 2024 оны 04-р сарын 10
  date = new Date(date);
  const day = String(date.getDate());
  const month = String(date.getMonth() + 1);
  const year = String(date.getFullYear());
  return `${year} оны ${month}-р сарын ${day}`;
}

export function formatDateTimeLong(date: Date) { // 2024-04-10T12:00:00.000Z => 2024 оны 04-р сарын 10 12:00
  date = new Date(date);
  const day = String(date.getDate());
  const month = String(date.getMonth() + 1);
  const year = String(date.getFullYear());
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year} оны ${month}-р сарын ${day} ${hours}:${minutes}`;
}

export function calcBetweenDates(start: Date, end: Date) { // 2024-04-10T12:00:00.000Z, 2024-04-15T12:00:00.000Z => 5
  const startDate = new Date(start)
  const endDate = new Date(end)
  const diff = endDate.getTime() - startDate.getTime()
  const days = diff / (1000 * 60 * 60 * 24)
  return days + 1
}

export function formatTime(seconds: number) { // 3600 => 01:00:00
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor(seconds / 60) - (hours * 60)
  seconds = seconds % 60
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}


export function urlBase64ToUint8Array(base64String: any) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding)
    .replace(/\\-/g, '+')
    .replace(/_/g, '/')

  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

export function bytesToSize(bytes: number) {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  if (bytes == 0) return '0 Byte';
  const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)).toString());
  return Math.round(bytes / Math.pow(1024, i)) + ' ' + sizes[i];
}

export const formatTextLinks = (text: string) => {
  if (!text) return ''
  const urlPattern = /((http|https):\/\/[^\s/$.?#].[^\s]*)|(www\.[^\s/$.?#].[^\s]*)/gi
  return text.replace(urlPattern, (match) => {
    const url = match.startsWith('http') ? match : `https://${match}`
    return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="underline font-semibold">${match}</a>`
  })
}

export function removeHTMLTags(str: string) {
  if (!str) return '';
  return str.replace(/<\/?[^>]+(>|$)/g, '');
}

export function checkSegment(segment: string | null) {
  if (segment === null) return '/'
  return '/' + segment
}

export function calculateRemainingTime(expireAt: string) {
  if (!expireAt) return ''

  const now = new Date()
  const expiration = new Date(expireAt)
  const diffInSeconds = Math.floor((expiration.getTime() - now.getTime()) / 1000)

  if (diffInSeconds <= 0) return 'Хугацаа дууссан'

  const days = Math.floor(diffInSeconds / (60 * 60 * 24))
  const hours = Math.floor(diffInSeconds / (60 * 60)) - (days * 24)
  const minutes = Math.floor(diffInSeconds / 60) - (days * 24 * 60) - (hours * 60)
  const seconds = diffInSeconds - (days * 24 * 60 * 60) - (hours * 60 * 60) - (minutes * 60)

  let time = ''
  if (days > 0) time += days + ' хоног '
  if (hours > 0) time += hours + ' цаг '
  if (minutes > 0) time += minutes + ' мин '
  if (seconds > 0 && days === 0 && hours === 0) time += seconds + ' сек'

  return time.trim()
}

export function getBorderRadiusClass(border?: string) {
  switch (border) {
  case "none": return "rounded-none"
  case "xs": return "rounded-xs"
  case "sm": return "rounded-sm"
  case "md": return "rounded-md"
  case "lg": return "rounded-lg"
  case "xl": return "rounded-xl"
  default: return "rounded-md"
  }
}

export const getAspectRatioClass = (aspectRatio?: string) => {
  if (!aspectRatio) return 'aspect-w-1 aspect-h-1'

  switch (aspectRatio) {
  case '1/1':
    return 'aspect-w-1 aspect-h-1'
  case '3/4':
    return 'aspect-w-3 aspect-h-4'
  case '4/3':
    return 'aspect-w-4 aspect-h-3'
  case '16/9':
    return 'aspect-w-16 aspect-h-9'
  case '9/16':
    return 'aspect-w-9 aspect-h-16'
  default:
    return 'aspect-w-1 aspect-h-1'
  }
}

export const getFontClass = (font?: string) => {
  if (!font) return 'font-inter'

  switch (font.toLowerCase()) {
  case 'gilroy':
    return 'font-gilroy'
  case 'inter':
    return 'font-inter'
  case 'roboto':
    return 'font-roboto'
  case 'montserrat':
    return 'font-montserrat'
  case 'playpen':
    return 'font-playpen'
  case 'cormorant':
    return 'font-cormorant'
  default:
    return 'font-inter'
  }
}

export const getBackgroundColorClass = (color?: string) => {
  if (!color) return 'bg-gradient'

  if (color.startsWith('#')) {
    return `text-white transition-all duration-400 ease-out hover:shadow-md hover:opacity-90`
  }

  switch (color) {
  case 'blue-500':
    return 'bg-blue-500 hover:bg-blue-500/90 text-white transition-all duration-400 ease-out hover:shadow-md hover:shadow-blue-500/15'
  case 'red-500':
    return 'bg-red-500 hover:bg-red-500/90 text-white transition-all duration-400 ease-out hover:shadow-md hover:shadow-red-500/15'
  case 'pink-500':
    return 'bg-pink-500 hover:bg-pink-500/90 text-white transition-all duration-400 ease-out hover:shadow-md hover:shadow-pink-500/15'
  case 'purple-500':
    return 'bg-purple-500 hover:bg-purple-500/90 text-white transition-all duration-400 ease-out hover:shadow-md hover:shadow-purple-500/15'
  case 'green-500':
    return 'bg-green-500 hover:bg-green-500/90 text-white transition-all duration-400 ease-out hover:shadow-md hover:shadow-green-500/15'
  case 'amber-500':
    return 'bg-amber-500 hover:bg-amber-500/90 text-white transition-all duration-400 ease-out hover:shadow-md hover:shadow-amber-500/15'
  case 'zinc-800':
    return 'bg-zinc-800 hover:bg-zinc-800/90 text-white transition-all duration-400 ease-out hover:shadow-md hover:shadow-zinc-800/15'
  default:
    return 'bg-blue-500 hover:bg-blue-500/90 text-white transition-all duration-400 ease-out hover:shadow-md hover:shadow-blue-500/15'
  }
}

export const getBackgroundColorStyles = (color?: string): { className: string; style: React.CSSProperties } => {
  if (!color) return { className: 'bg-gradient', style: {} }

  if (color.startsWith('#')) {
    return {
      className: 'text-white transition-all duration-400 ease-out hover:shadow-md',
      style: {
        backgroundColor: color,
        '--hover-color': color + 'e6',
      } as React.CSSProperties
    }
  }

  return {
    className: getBackgroundColorClass(color),
    style: {}
  }
}

export const getMutedBackgroundColorClass = (color?: string) => {
  if (!color) return 'bg-blue-50'

  if (color.startsWith('#')) {
    return 'transition-colors'
  }

  switch (color) {
  case 'blue-500':
    return 'bg-blue-50 transition-colors'
  case 'red-500':
    return 'bg-red-50 transition-colors'
  case 'pink-500':
    return 'bg-pink-50 transition-colors'
  case 'purple-500':
    return 'bg-purple-50 transition-colors'
  case 'green-500':
    return 'bg-green-50 transition-colors'
  case 'amber-500':
    return 'bg-amber-50 transition-colors'
  case 'zinc-800':
    return 'bg-zinc-800/10 transition-colors'
  default:
    return 'bg-blue-50 transition-colors'
  }
}

export const getMutedBackgroundColorStyles = (color?: string): { className: string; style: React.CSSProperties } => {
  if (!color) return { className: 'bg-blue-50', style: {} }

  if (color.startsWith('#')) {
    return {
      className: 'transition-colors',
      style: {
        backgroundColor: color + '1a',
      }
    }
  }

  return {
    className: getMutedBackgroundColorClass(color),
    style: {}
  }
}

export const getTextColorClass = (color?: string) => {
  if (!color) return 'text-blue-500'

  if (color.startsWith('#')) {
    return ''
  }

  switch (color) {
  case 'blue-500':
    return 'text-blue-500'
  case 'red-500':
    return 'text-red-500'
  case 'pink-500':
    return 'text-pink-500'
  case 'purple-500':
    return 'text-purple-500'
  case 'green-500':
    return 'text-green-500'
  case 'amber-500':
    return 'text-amber-500'
  case 'zinc-800':
    return 'text-zinc-800'
  default:
    return 'text-blue-500'
  }
}

export const getTextColorStyles = (color?: string): { className: string; style: React.CSSProperties } => {
  if (!color) return { className: 'text-blue-500', style: {} }

  if (color.startsWith('#')) {
    return {
      className: '',
      style: { color: color }
    }
  }

  return {
    className: getTextColorClass(color),
    style: {}
  }
}

export const getFillColorClass = (color?: string) => {
  if (!color) return 'fill-blue-500'

  if (color.startsWith('#')) {
    return ''
  }

  switch (color) {
  case 'blue-500':
    return 'fill-blue-500'
  case 'red-500':
    return 'fill-red-500'
  case 'pink-500':
    return 'fill-pink-500'
  case 'purple-500':
    return 'fill-purple-500'
  case 'green-500':
    return 'fill-green-500'
  case 'amber-500':
    return 'fill-amber-500'
  case 'zinc-800':
    return 'fill-zinc-800'
  default:
    return 'fill-blue-500'
  }
}

export const getFillColorStyles = (color?: string): { className: string; style: React.CSSProperties } => {
  if (!color) return { className: 'fill-blue-500', style: {} }

  if (color.startsWith('#')) {
    return {
      className: '',
      style: { fill: color }
    }
  }

  return {
    className: getFillColorClass(color),
    style: {}
  }
}

export const getBorderColorClass = (color?: string) => {
  if (!color) return 'border-blue-500'

  if (color.startsWith('#')) {
    return ''
  }

  switch (color) {
  case 'blue-500':
    return 'border-blue-500'
  case 'red-500':
    return 'border-red-500'
  case 'pink-500':
    return 'border-pink-500'
  case 'purple-500':
    return 'border-purple-500'
  case 'green-500':
    return 'border-green-500'
  case 'amber-500':
    return 'border-amber-500'
  case 'zinc-800':
    return 'border-zinc-800'
  default:
    return 'border-blue-500'
  }
}

export const getBorderColorStyles = (color?: string): { className: string; style: React.CSSProperties } => {
  if (!color) return { className: 'border-blue-500', style: {} }

  if (color.startsWith('#')) {
    return {
      className: '',
      style: { borderColor: color }
    }
  }

  return {
    className: getBorderColorClass(color),
    style: {}
  }
}

export const getRingColorClass = (color?: string) => {
  if (!color) return 'ring-blue-500'

  if (color.startsWith('#')) {
    return ''
  }

  switch (color) {
  case 'blue-500':
    return 'ring-blue-500'
  case 'red-500':
    return 'ring-red-500'
  case 'pink-500':
    return 'ring-pink-500'
  case 'purple-500':
    return 'ring-purple-500'
  case 'green-500':
    return 'ring-green-500'
  case 'amber-500':
    return 'ring-amber-500'
  case 'zinc-800':
    return 'ring-zinc-800'
  default:
    return 'ring-blue-500'
  }
}

export const getRingColorStyles = (color?: string): { className: string; style: React.CSSProperties } => {
  if (!color) return { className: 'ring-blue-500', style: {} }

  if (color.startsWith('#')) {
    return {
      className: 'ring-2 ring-offset-2',
      style: {
        '--tw-ring-color': color,
        '--tw-ring-offset-color': '#ffffff'
      } as React.CSSProperties
    }
  }

  return {
    className: getRingColorClass(color),
    style: {}
  }
}

export const getFocusRingColorClass = (color?: string) => {
  if (!color) return 'focus:ring-blue-500'

  if (color.startsWith('#')) {
    return ''
  }

  switch (color) {
  case 'blue-500':
    return 'focus:ring-blue-500'
  case 'red-500':
    return 'focus:ring-red-500'
  case 'pink-500':
    return 'focus:ring-pink-500'
  case 'purple-500':
    return 'focus:ring-purple-500'
  case 'green-500':
    return 'focus:ring-green-500'
  case 'amber-500':
    return 'focus:ring-amber-500'
  case 'zinc-800':
    return 'focus:ring-zinc-800'
  default:
    return 'focus:ring-blue-500'
  }
}

export const getFocusRingColorStyles = (color?: string): { className: string; style: React.CSSProperties } => {
  if (!color) return { className: 'focus:ring-blue-500', style: {} }

  if (color.startsWith('#')) {
    return {
      className: 'focus:ring-2 focus:ring-offset-2',
      style: {
        '--tw-ring-color': color
      } as React.CSSProperties
    }
  }

  return {
    className: getFocusRingColorClass(color),
    style: {}
  }
}

export const getVendorPrimaryColor = (vendor: any): string => {
  if (vendor?.color?.primary) {
    return vendor.color.primary
  }
  if (vendor?.primary) {
    return vendor.primary
  }
  return 'blue-500'
}

const TAILWIND_TOKEN_HEX: Record<string, string> = {
  'blue-500':   '#3b82f6',
  'red-500':    '#ef4444',
  'pink-500':   '#ec4899',
  'purple-500': '#a855f7',
  'green-500':  '#22c55e',
  'amber-500':  '#f59e0b',
  'zinc-800':   '#27272a',
}

export function resolveColorHex(color?: string): string {
  if (!color) return TAILWIND_TOKEN_HEX['blue-500']
  if (color.startsWith('#')) return color
  return TAILWIND_TOKEN_HEX[color] || TAILWIND_TOKEN_HEX['blue-500']
}

export function isColorBright(color?: string): boolean {
  const hex = resolveColorHex(color).replace('#', '')
  if (hex.length !== 6) return false
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.6
}

export function getContrastTextColor(color?: string): string {
  return isColorBright(color) ? '#18181b' : '#ffffff'
}

type OrderBadgeVariant = "green" | "red" | "amber" | "purple" | "blue" | "rose" | undefined

const orderStatusMeta: Record<string, { label: string, variant: OrderBadgeVariant }> = {
  canceled: { label: 'Цуцлагдсан', variant: 'red' },
  refunded: { label: 'Бүтэн буцаасан', variant: 'purple' },
  partiallyRefunded: { label: 'Хагас буцаасан', variant: 'rose' },
  pending: { label: 'Хүлээгдэж буй', variant: 'amber' },
  paid: { label: 'Төлөгдсөн', variant: 'green' },
  ready: { label: 'Бэлэн болсон', variant: 'blue' },
  shipped: { label: 'Хүргэлтэнд гарсан', variant: undefined },
  delivered: { label: 'Хүргэгдсэн', variant: 'blue' },
  deliveryFailed: { label: 'Хүргэлт амжилтгүй', variant: 'red' },
}

export function getOrderStatusLabel(status: string): string {
  return orderStatusMeta[status]?.label ?? 'Тодорхойгүй'
}

export function getOrderStatusBadge(status: string) {
  return <CustomBadge variant={orderStatusMeta[status]?.variant}>{getOrderStatusLabel(status)}</CustomBadge>
}

// Badge variant per Booklo delivery statusCode (mirrors the backend parseBookloStatus).
const bookloBadgeVariant: Record<string, "green" | "red" | "amber" | "purple" | "blue" | "rose" | undefined> = {
  pending: "amber",
  in_delivery: undefined, // default (neutral) — matches order 'shipped'
  delivered: "blue",
  delivery_failed: "red",
  postponed: "amber",
  canceled: "red",
  not_arrived: "amber",
}

// Shorter display labels for verbose Booklo status strings.
const bookloBadgeLabel: Record<string, string> = {
  postponed: "Хойшлогдсон",
}

// Order badge that prefers Booklo's delivery status for orders actually dispatched
// through Booklo (bookloOrderId set + a synced bookloStatus). Surfaces richer Booklo
// states (Бараа ирээгүй, Хойшлогдсон, …); falls back to getOrderStatusBadge otherwise.
export function getOrderDeliveryLabel(order: any): string {
  const booklo = order?.bookloStatus
  if (order?.bookloOrderId && booklo?.status) return bookloBadgeLabel[booklo.statusCode] ?? booklo.status
  return getOrderStatusLabel(order?.status)
}

export function getOrderDeliveryBadge(order: any) {
  const booklo = order?.bookloStatus
  if (order?.bookloOrderId && booklo?.status) {
    return <CustomBadge variant={bookloBadgeVariant[booklo.statusCode]}>{getOrderDeliveryLabel(order)}</CustomBadge>
  }
  return getOrderStatusBadge(order?.status)
}

// Is this order's money in hand? For every prepaid order the status badge already
// says so — nothing reaches "Бэлэн болсон" without passing "Төлөгдсөн" first. A
// pay-after-delivery order moves on both axes independently, so it needs its own
// badge; paidAt is the only trustworthy signal there.
function getOrderPaymentMeta(order: any): { label: string, variant: OrderBadgeVariant } | null {
  if (!order?.payAfterDelivery) return null
  // The status badge already says "Төлбөр баталгаажсан" at that point; rendering both
  // gives two identical chips. See the vendor panel's copy of this note.
  if (order?.status === 'paid') return null
  if (order?.paidAt) return { label: 'Төлөгдсөн', variant: 'green' }
  // Split payment: the deposit is in but the balance is not. Blue rather than green
  // (which would claim the order is settled) or amber (which means nothing arrived).
  if (order?.depositPaidAt) return { label: 'Урьдчилгаа төлсөн', variant: 'blue' }
  return { label: 'Төлөөгүй', variant: 'amber' }
}

export function getOrderPaymentLabel(order: any): string | null {
  return getOrderPaymentMeta(order)?.label ?? null
}

export function getOrderPaymentBadge(order: any) {
  const meta = getOrderPaymentMeta(order)
  return meta ? <CustomBadge variant={meta.variant}>{meta.label}</CustomBadge> : null
}

export function getGiftCardStatusBadge(status: string) {
  switch (status) {
  case 'canceled':
    return <CustomBadge variant="red">Цуцлагдсан</CustomBadge>
  case 'pending':
    return <CustomBadge variant="amber">Хүлээгдэж буй</CustomBadge>
  case 'paid':
    return <CustomBadge variant="green">Төлөгдсөн</CustomBadge>
  case 'sent':
    return <CustomBadge variant="blue">Илгээгдсэн</CustomBadge>
  case 'used':
    return <CustomBadge variant="purple">Ашиглагдсан</CustomBadge>
  default:
    return <CustomBadge>Тодорхойгүй</CustomBadge>
  }
}

export function getAddressTypeBadge(type: string) {
  switch (type) {
  case 'apartment':
    return <CustomBadge className="text-xs px-2 py-1" variant="blue">Орон сууц</CustomBadge>
  case 'house':
    return <CustomBadge className="text-xs px-2 py-1" variant="green">Хашаа байшин</CustomBadge>
  case 'office':
    return <CustomBadge className="text-xs px-2 py-1" variant="purple">Оффис</CustomBadge>
  default:
    return <CustomBadge className="text-xs px-2 py-1">Тодорхойгүй</CustomBadge>
  }
}

export const bankList = [
  { id: 1, code: '010000', nameEn: 'Bank of Mongolia', nameMn: 'Монгол банк', icon: '/mongol.jpg' },
  { id: 2, code: '020000', nameEn: 'Capital bank', nameMn: 'Капитал банк', icon: '/capital.png' },
  { id: 3, code: '040000', nameEn: 'Trade and Development bank of Mongolia', nameMn: 'Худалдаа хөгжлийн банк', icon: '/tdb.png' },
  { id: 4, code: '050000', nameEn: 'Khan bank', nameMn: 'Хаан банк', icon: '/khaan.jpg' },
  { id: 5, code: '150000', nameEn: 'Golomt bank', nameMn: 'Голомт банк', icon: '/golomt.png' },
  { id: 6, code: '190000', nameEn: 'Trans bank', nameMn: 'Тээвэр хөгжлийн банк', icon: '/trans.jpeg' },
  { id: 7, code: '210000', nameEn: 'Arig bank', nameMn: 'Ариг банк', icon: '/arig.png' },
  { id: 8, code: '220000', nameEn: 'Credit bank', nameMn: 'Кредит банк', icon: '/credit.jpg' },
  { id: 9, code: '290000', nameEn: 'NIB bank', nameMn: 'ҮХО банк', icon: '/nib.jpg' },
  { id: 10, code: '300000', nameEn: 'Capitron bank', nameMn: 'Капитрон банк', icon: '/capitron.jpg' },
  { id: 11, code: '320000', nameEn: 'Xac bank', nameMn: 'Хас банк', icon: '/khas.png' },
  { id: 12, code: '330000', nameEn: 'Chingiskhan bank', nameMn: 'Чингисхаан банк', icon: '/chingiss.jpg' },
  { id: 13, code: '340000', nameEn: 'State bank', nameMn: 'Төрийн банк', icon: '/state.jpg' },
  { id: 14, code: '360000', nameEn: 'National Development bank', nameMn: 'Хөгжлийн банк', icon: '/dev.jpg' },
  { id: 15, code: '380000', nameEn: 'Bogd bank', nameMn: 'Богд банк', icon: '/bogd.jpg' },
  { id: 16, code: '900000', nameEn: 'State fund', nameMn: 'Төрийн сан', icon: '/san.png' },
  { id: 17, code: '500000', nameEn: 'Mobi Finance', nameMn: 'Мобифинанс', icon: '/mobifinance.png' },
  { id: 18, code: '390000', nameEn: 'M bank', nameMn: 'М банк', icon: '/mbank.png' },
  { id: 20, code: '993000', nameEn: 'Invescore', nameMn: 'Инвэскор ББСБ', icon: '/invescore.jpg' },
  { id: 21, code: '100000', nameEn: 'Test bank', nameMn: 'Тест банк', icon: '/golomt.png' },
]

export function filterDiscounts(discounts: any[], isLoggedIn: boolean, variantId?: string) {
  if (!discounts?.length) return discounts
  let filtered = discounts
  if (!isLoggedIn) filtered = filtered.filter((d: any) => !d.customerOnly)
  if (variantId) filtered = filtered.filter((d: any) => !d.variantIds || d.variantIds.length === 0 || d.variantIds.includes(variantId))
  return filtered
}

export function getProductDiscount(discounts: any[] | null) {
  if (!discounts || !Array.isArray(discounts)) return null
  return discounts.find((d: any) => d.type === 'productDiscount') || null
}

export function getBuyXGetYDiscount(discounts: any[] | null) {
  if (!discounts || !Array.isArray(discounts)) return null
  return discounts.find((d: any) => d.type === 'buyXgetY') || null
}

export function discountAppliesToVariant(discount: any, variant: any) {
  if (!discount || !variant) return false
  if (!discount.variantIds || discount.variantIds.length === 0) return true
  return discount.variantIds.includes(variant.id)
}

export function getDiscountedPrice(price: number, priceSale: number | null, discounts: any[]) {
  if (!price || !discounts?.length) return null
  let best = price
  for (const d of discounts) {
    if (d.type !== 'productDiscount') continue
    if (d.method === 'code') continue
    const basePrice = d.discountBase === 'sale' && priceSale && priceSale > 0 && priceSale < price ? priceSale : price
    let discounted = basePrice
    if (d.valueType === 'percent' && d.valuePercent) {
      discounted = Math.round(basePrice * (1 - d.valuePercent / 100))
    } else if (d.valueType === 'amount' && d.valueAmount) {
      discounted = basePrice - d.valueAmount
    } else if (d.valueType === 'free') {
      discounted = 0
    }
    if (discounted < 0) discounted = 0
    if (discounted < best) best = discounted
  }
  return best < price ? best : null
}

export function getEffectivePrice(variant: any, discounts: any[]) {
  if (!variant?.price || variant.price <= 0) return null
  const priceSale = variant.priceSale && variant.priceSale > 0 && variant.priceSale < variant.price ? variant.priceSale : null
  const hasDiscount = discounts?.some((d: any) => d.type === 'productDiscount' && d.method !== 'code')
  if (hasDiscount) {
    const discountedPrice = getDiscountedPrice(variant.price, priceSale, discounts)
    if (discountedPrice !== null && priceSale !== null) {
      return Math.min(discountedPrice, priceSale)
    }
    return discountedPrice
  }
  return priceSale ?? null
}

export function getDisplayDiscounts(product: any, isLoggedIn: boolean, variantId?: string) {
  const all = filterDiscounts(product?.discounts, isLoggedIn, variantId)
  return all?.filter((d: any) => !(d.type === 'buyXgetY' && d.role === 'reward')) || []
}

export function getProductDisplayPrice(product: any, variant: any, isLoggedIn: boolean) {
  const discounts = getDisplayDiscounts(product, isLoggedIn, variant?.id)
  const effective = getEffectivePrice(variant, discounts)
  return effective ?? variant?.price ?? 0
}

export function isDiscountBetter(variant: any, discounts: any[]) {
  if (!variant?.price || variant.price <= 0 || !discounts?.length) return false
  if (discounts.some((d: any) => d.type === 'buyXgetY')) return true
  const priceSale = variant.priceSale && variant.priceSale > 0 && variant.priceSale < variant.price ? variant.priceSale : null
  if (!priceSale) return discounts.some((d: any) => d.type === 'productDiscount' && d.method !== 'code')
  const discountedPrice = getDiscountedPrice(variant.price, priceSale, discounts)
  return discountedPrice !== null && discountedPrice < priceSale
}

export function getStrikethroughPrice(variant: any, discounts: any[]) {
  if (!variant?.price || variant.price <= 0) return variant?.price ?? 0
  const priceSale = variant.priceSale && variant.priceSale > 0 && variant.priceSale < variant.price ? variant.priceSale : null
  const hasDiscount = discounts?.some((d: any) => d.type === 'productDiscount' && d.method !== 'code')
  if (hasDiscount && priceSale) {
    const discountedPrice = getDiscountedPrice(variant.price, priceSale, discounts)
    if (discountedPrice !== null && priceSale <= discountedPrice) return variant.price
  }
  const saleBaseDiscount = discounts?.find((d: any) => d.type === 'productDiscount' && d.method !== 'code' && d.discountBase === 'sale')
  if (saleBaseDiscount && priceSale) return priceSale
  return variant.price
}

export function getRewardItemPrice(product: any) {
  if (!product?.variant) return 0
  const variant = product.variant
  if (product.valueType === 'free') return 0
  const basePrice = product.discountBase === 'sale' && variant.priceSale && variant.priceSale > 0 && variant.priceSale < variant.price
    ? variant.priceSale
    : variant.price
  if (product.valueType === 'percent' && product.valuePercent) {
    return Math.max(0, Math.round(basePrice * (1 - product.valuePercent / 100)))
  }
  if (product.valueType === 'amount' && product.valueAmount) {
    return Math.max(0, basePrice - product.valueAmount)
  }
  return basePrice
}

export function getCartTotal(products: any[], isLoggedIn: boolean) {
  return products.reduce((sum: number, product: any) => {
    if (product.isRewardItem) {
      const rewardPrice = getRewardItemPrice(product)
      return sum + (product.quantity || 0) * rewardPrice
    }
    const productData = product.product || product
    const variant = product.variant
    const discounts = filterDiscounts(productData?.discounts, isLoggedIn, variant?.id)
    const effectivePrice = getEffectivePrice(variant, discounts)
    const unitPrice = effectivePrice ?? variant?.price ?? 0
    return sum + (product.quantity || 0) * (unitPrice || 0)
  }, 0)
}

export function getMainDomain(vendor: any) {
  if(!vendor || !vendor.domain) return null
  if(vendor.domains && vendor.domains.length > 0) return vendor.domains?.[0]?.domain
  if(vendor.customDomain && vendor.isCustomDomainActive) return vendor.customDomain
  return `${vendor.domain}.shop.mn`
}

export function isAuthRoute(pathname: string | null | undefined) {
  if (!pathname) return false
  if (pathname.includes('/checkout/login')) return false
  return pathname.endsWith('/login') || pathname.endsWith('/register') || pathname.endsWith('/forgot') || pathname.endsWith('/reset') || pathname.endsWith('/logout')
}

export function canShowCustomerChat(vendor: any, subscription: any) {
  return subscription?.status === 'active' && subscription?.canUseCustomerChat && vendor?.modules?.includes('ai-chatbot') && !!vendor?.customerChatEnabled
}

const parsePgArrayOrCsvString = (value: any): string[] => {
  if (!value) return []
  if (Array.isArray(value)) return value
  if (typeof value !== 'string') return []
  if (value.startsWith('{') && value.endsWith('}')) {
    return value.substring(1, value.length - 1).split(',').map(s => s.trim())
  }
  return value.split(',').map(s => s.trim())
}

export function getProductTags(product: any): string[] {
  return parsePgArrayOrCsvString(product?.tags).filter(t => t !== '')
}

export function getYoutubeVideos(product: any): string[] {
  return parsePgArrayOrCsvString(product?.youtubeVideos).filter(u => u !== '')
}

export function getYoutubeVideoId(url: string): string | null {
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/)
  return match ? match[1] : null
}

export function getSalePercent(variant: any): number {
  if (!variant?.priceSale || !variant?.price || variant.priceSale >= variant.price) return 0
  const p = Math.round((1 - variant.priceSale / variant.price) * 100)
  return p === 100 && variant.priceSale > 0 ? 99 : p
}

export function getOtherVariantSaleType(product: any, currentVariantId: string | undefined, isLoggedIn: boolean): 'discount' | 'price' | null {
  if (!product?.variants || product.variants.length <= 1) return null
  let hasPriceSale = false
  let hasDiscountSale = false
  for (const v of product.variants) {
    if (v.id === currentVariantId) continue
    if (v.priceSale && v.priceSale > 0 && v.priceSale < v.price) hasPriceSale = true
    const vDiscounts = getDisplayDiscounts(product, isLoggedIn, v.id)
    if (vDiscounts?.some((d: any) => d.type === 'productDiscount' && d.method !== 'code') || vDiscounts?.some((d: any) => d.type === 'buyXgetY')) hasDiscountSale = true
  }
  if (hasDiscountSale) return 'discount'
  if (hasPriceSale) return 'price'
  return null
}

export function isProductInStock(product: any): boolean {
  if (!product) return false
  if (!product.isTrackable) return true
  if (product.variants && product.variants.length > 0) {
    return product.variants.some((v: any) => v.stock > 0)
  }
  if (product.variant?.stock !== undefined && product.variant?.stock !== null) {
    return product.variant.stock > 0
  }
  if (product.stock !== undefined && product.stock !== null) {
    return product.stock > 0
  }
  return true
}

export function filterAvailableProducts(productsList: any[], excludeId?: string): any[] {
  if (!productsList || productsList.length === 0) return []
  return productsList
    .filter((p: any) => !excludeId || p.id !== excludeId)
    .filter((p: any) => isProductInStock(p))
}

export function getSuggestedProducts(currentProduct: any, productsList: any[], limit = 5): any[] {
  const available = filterAvailableProducts(productsList, currentProduct?.id)
  const currentCategoryIds = currentProduct?.categories?.map((c: any) => c.id) || []
  const categoryMatches = available.filter((p: any) => {
    if (!currentCategoryIds.length || !p.categories?.length) return false
    const productCategoryIds = p.categories.map((c: any) => c.id)
    return productCategoryIds.some((id: string) => currentCategoryIds.includes(id))
  })
  const matchIds = new Set(categoryMatches.map((p: any) => p.id))
  const rest = available.filter((p: any) => !matchIds.has(p.id))
  const head = categoryMatches.slice(0, limit)
  return [...head, ...rest.slice(0, limit - head.length)]
}

export function getBuyXTriggerCount(discount: any): number {
  const buyX = discount?.buyXMinQuantity || 1
  if (discount?.buyXMatchMode === 'all') {
    const triggerCount = discount?.triggerTargets?.length || discount?.triggerProducts?.length || 1
    return triggerCount * buyX
  }
  return buyX
}

export function getBuyXGetYRewardRule(info: any): string {
  if (info?.getYMatchMode === 'all') return `бэлэг тус бүрээс ${info.maxRewardCount}`
  if (info?.getYMatchMode === 'custom') return `${info.getYProductLimit} төрлөөс нийт ${info.maxRewardCount} хөнгөлөлттэй`
  return `${info?.maxRewardCount} хөнгөлөлттэй`
}

export function getBuyXGetYTagText(discount: any): string {
  const buyX = getBuyXTriggerCount(discount)
  const reward = discount?.rewardProducts?.[0] || {}
  const rewardType = reward.valueType || discount?.valueType
  const isFree = rewardType === 'free' || !rewardType
  if (isFree) return 'Бэлэгтэй'
  const pct = reward.valuePercent || discount?.valuePercent || 0
  return `${buyX}+${pct}%`
}

export function getBuyXGetYFallbackTitle(discount: any): string {
  const buyX = discount?.buyXMinQuantity || 1
  const getY = discount?.getYMaxQuantity || 1
  const reward = discount?.rewardProducts?.[0] || {}
  const rewardType = reward.valueType || discount?.valueType
  const isFree = rewardType === 'free' || !rewardType
  if (isFree) return `${buyX} авбал ${getY} үнэгүй`
  const pct = reward.valuePercent || discount?.valuePercent || 0
  return `${buyX} авбал ${getY}-г ${pct}% хямдралтай`
}

export function getActiveBanners(banners: any[]): any[] {
  if (!banners || banners.length === 0) return []
  const now = Date.now()
  return banners.filter((banner: any) => {
    const start = banner.startsAt ? new Date(banner.startsAt).getTime() : null
    const end = banner.endsAt ? new Date(banner.endsAt).getTime() : null
    if (start !== null && Number.isFinite(start) && start > now) return false
    if (end !== null && Number.isFinite(end) && end < now) return false
    return true
  })
}

export function formatFileSize(bytes: number): string {
  if (!bytes || bytes < 0) return '0 B'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(bytes < 1024 * 10 ? 1 : 0)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(bytes < 1024 * 1024 * 10 ? 2 : 1)} MB`
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`
}

export function getVariantDimensions(variant: any): string | null {
  const dims: string[] = []
  if (variant?.weight) dims.push(`Жин: ${variant.weight}${variant.weightUnit ? ` ${variant.weightUnit}` : ''}`)
  if (variant?.length) dims.push(`Урт: ${variant.length}${variant.lengthUnit ? ` ${variant.lengthUnit}` : ''}`)
  if (variant?.height) dims.push(`Өндөр: ${variant.height}${variant.heightUnit ? ` ${variant.heightUnit}` : ''}`)
  if (variant?.width) dims.push(`Өргөн: ${variant.width}${variant.widthUnit ? ` ${variant.widthUnit}` : ''}`)
  return dims.length > 0 ? dims.join(', ') : null
}